import { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import Loading from 'components/Loading';
import './BookkeeperLocationForm.scss';
import { LocalErrorBoundary } from 'components/ErrorBoundary';
import {
  IconRefresh,
  IconLink,
  IconEdit,
  IconClose,
  IconPendoInfo,
  IconDownload,
  IconCopyNew,
  IconQboNew,
  IconNavPlFilled,
  IconViewData,
} from '../../../../../components/Icons';
import { isProdHost, prefixClass, TEN_SECONDS, reportingOrigin } from 'lib/utils';
import Modal from 'react-modal';
import GenerateReportsForm from 'components/GenerateReportsForm';

import {
  showToast,
  dismissToast,
  TOAST_DEFAULT,
  TOAST_SUCCESS,
  TOAST_ERROR,
  ONE_MINUTE,
  HALF_MINUTE,
  FIVE_SECONDS,
} from 'lib/utils';

import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import { useHoneReportTemplates } from 'components/HoneReportTemplates';
import { useLocationsStore, getLocationClassList, syncToken } from 'hooks/useLocationsStore';
import { useHoneLocations } from 'components/HoneLocations';

import { useAuthContext } from 'context/useAuthContext';
import { useRecentLocations } from '../../../../../hooks/useRecentLocations';
import { Location as KsLocation, LocationClass, QBOEnvironment, RollfiCompany } from '@hone-automation/common';
import KSButton from 'presentation/components/KSButton';
import KSToggleButton from 'presentation/components/KSToggleButton';
import { Option } from 'types';
import { set } from 'lodash';
import { Tooltip, Whisper } from 'rsuite';
import classNames from 'classnames';
import { useGenerateLocationDescription } from 'hooks/ai';
import { useGetRollfiCompanies } from 'hooks/rollfi';

const TOAST_GENERATE_TEMPLATES_TEXT = 'Generating templates, will take a moment to complete';

interface FormData {
  name: string;
  org: string;
  weekStart: string;
  askCode: string;
  qboEnv: string;
  qboId: string;
  class: string;
  dept: string;
  link: string;
  emailId: string;
  paymentLink: string;
  v2Link: string;
  payrollLink: string;
  tasksLink: string;
  teamsLink: string;
  description: string;
  config: {
    links: {
      link: string;
      paymentLink: string;
      v2Link: string;
      payrollLink: string;
      tasksLink: string;
      teamsLink: string;
    };
    description: string;
    companyName?: string;
    legalName?: string;
    transactionAttachments?: boolean;
    includeDeletedAccounts?: boolean;
    website?: string;
    offlineVendorGLs?: string;
    rollfi: {
      env: string;
      companyId: string;
    };
  };
}

function BookkeeperLocationForm(): JSX.Element {
  const defaultRollfiEnv = isProdHost() ? 'prod' : 'dev';
  const { recentLocations, setRecentLocations } = useRecentLocations();

  const weekDays: Option[] = [
    { label: 'Sunday', value: 'Su' },
    { label: 'Monday', value: 'Mo' },
    { label: 'Tuesday', value: 'Tu' },
    { label: 'Wednesday', value: 'We' },
    { label: 'Thursday', value: 'Th' },
    { label: 'Friday', value: 'Fr' },
    { label: 'Saturday', value: 'Sa' },
  ];

  const {
    status: locationsStatus,
    currentLocationId,
    setCurrentLocationId,
    currentLocation,
    fetchAllLocations,
  } = useLocationsStore();
  const { saveLocation } = useHoneLocations();

  const {
    data: aiGenerationPrompt,
    isPending: aiLoading,
    mutate: generateAiPrompts,
  } = useGenerateLocationDescription({ locationId: currentLocationId! });

  const { data: rollfiCompaniesData } = useGetRollfiCompanies(currentLocationId);

  const { userId } = useAuthContext();

  const { generateTemplate } = useHoneReportTemplates();

  // Form management with react-hook-form
  const {
    control,
    handleSubmit,
    setValue,
    reset,
    formState: { isDirty, isSubmitting },
  } = useForm<FormData>({
    defaultValues: {
      name: '',
      org: '',
      weekStart: 'Su',
      askCode: '',
      qboEnv: 'Prod',
      qboId: '',
      emailId: '',
      class: '',
      dept: '',
      link: '',
      paymentLink: '',
      v2Link: '',
      payrollLink: '',
      tasksLink: '',
      teamsLink: '',
      description: '',
      config: {
        links: {
          link: '',
          paymentLink: '',
          v2Link: '',
          payrollLink: '',
          tasksLink: '',
          teamsLink: '',
        },
        description: '',
        companyName: '',
        legalName: '',
        transactionAttachments: false,
        includeDeletedAccounts: false,
        website: '',
        offlineVendorGLs: '',
        rollfi: {
          env: defaultRollfiEnv,
          companyId: '',
        },
      },
    },
  });

  // Non-form state
  const [id, setId] = useState('');
  const [tokenStatus, setTokenStatus] = useState('');
  const [classList, setClassList] = useState<LocationClass[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [showAiSuggestions, setShowAiSuggestions] = useState(false);

  // Edit state management
  const [editingFields, setEditingFields] = useState<Set<string>>(new Set());
  const [tempValues, setTempValues] = useState<{ [key: string]: string }>({});
  const [editingSections, setEditingSections] = useState<Set<string>>(new Set());

  const startEditing = (fieldName: string, currentValue: string) => {
    if (fieldName === 'section') {
      setEditingSections(prev => new Set(prev).add(currentValue));
      return;
    }
    setEditingFields(prev => new Set(prev).add(fieldName));
    setTempValues(prev => ({ ...prev, [fieldName]: currentValue }));
  };

  const stopEditingSection = (sectionName: string) => {
    setEditingSections(prev => {
      const newSet = new Set(prev);
      newSet.delete(sectionName);
      return newSet;
    });
  };

  const cancelEditing = (fieldName: string) => {
    setEditingFields(prev => {
      const newSet = new Set(prev);
      newSet.delete(fieldName);
      return newSet;
    });
    setTempValues(prev => {
      const newValues = { ...prev };
      delete newValues[fieldName];
      return newValues;
    });
  };

  const handleGetClasses = () => {
    if (!currentLocationId) {
      return;
    }
    getLocationClassList(currentLocationId)
      .then(result => {
        if (result && result.data) {
          setClassList(result.data);
          showToast(
            `Successfully refreshed class list: ${result.data.length} classes found in QBO`,
            TOAST_SUCCESS,
            FIVE_SECONDS
          );
        }
      })
      .catch(function (error: any) {
        showToast('Error getting class list for current location', TOAST_ERROR, HALF_MINUTE);
      });
  };

  const handleCopyId = () => {
    navigator.clipboard.writeText(id);
    showToast('Copied Location id to clipboard', TOAST_DEFAULT, FIVE_SECONDS);
  };

  const handleRefreshToken = () => {
    const qboEnv = currentLocation && currentLocation.qboEnv === 'Sandbox' ? '?qboEnv=Sandbox' : '';
    window.open(`${reportingOrigin()}/authUri${qboEnv}`, '_blank');
  };

  const handleSyncToken = () => {
    if (!currentLocationId) {
      showToast('Invalid location', TOAST_ERROR, TEN_SECONDS);
      return;
    }
    const toastId = showToast('Syncing Token', TOAST_DEFAULT, TEN_SECONDS);
    syncToken(currentLocationId)
      .then((result: any) => {
        dismissToast(toastId);
        if (result && result.data === 'ok') {
          showToast('Successfully synced token, good for 1 hour', TOAST_SUCCESS, FIVE_SECONDS);
        } else {
          showToast('Unable to sync token', TOAST_ERROR, TEN_SECONDS);
        }
      })
      .catch(function (error: any) {
        dismissToast(toastId);
        showToast('Error syncing token', TOAST_ERROR, TEN_SECONDS);
      });
  };

  const handleSaveLocation = handleSubmit(async (formData: FormData) => {
    const location: KsLocation = {
      id: currentLocationId!,
      name: formData.name,
      org: formData.org,
      status: 'Active',
      weekStart: formData.weekStart as WeekDay,
      askCode: formData.askCode,
      qboEnv: formData.qboEnv as QBOEnvironment,
      qboId: formData.qboId,
      class: formData.class,
      dept: formData.dept,
      emailId: formData.emailId,
      tokenStatus,
      link: formData.link,
      config: (currentLocation && currentLocation.config && Object.keys(currentLocation.config).length > 0
        ? {
            ...currentLocation.config,
            links: {
              ...(currentLocation.config.links || {}),
              paymentLink: formData.paymentLink,
              v2: formData.v2Link,
              payroll: formData.payrollLink,
              tasks: formData.tasksLink,
              teams: formData.teamsLink,
            },
            description: formData.description,
            companyName: formData.config.companyName || '',
            includeDeletedAccounts: formData.config.includeDeletedAccounts || false,
            legalName: formData.config.legalName || '',
            transactionAttachments: formData.config.transactionAttachments || false,
            website: formData.config.website || '',
            offlineVendorGLs: formData.config.offlineVendorGLs || '',
            rollfi: {
              env: formData.config.rollfi?.env || defaultRollfiEnv,
              companyId: formData.config.rollfi?.companyId || '',
            },
          }
        : {
            links: {
              paymentLink: formData.paymentLink,
              v2: formData.v2Link,
              payroll: formData.payrollLink,
              tasks: formData.tasksLink,
              teams: formData.teamsLink,
            },
            description: formData.description,
            rollfi: {
              env: formData.config.rollfi?.env || defaultRollfiEnv,
              companyId: formData.config.rollfi?.companyId || '',
            },
          }) as any,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    const toastId = showToast('Saving location, please wait a bit.', TOAST_DEFAULT, ONE_MINUTE);

    if (!userId) {
      dismissToast(toastId);
      showToast('No userId defined', TOAST_ERROR, FIVE_SECONDS);
      return;
    }

    saveLocation(location, userId)
      .then(async (result: any) => {
        if (result && result.data) {
          if (result.data.error !== undefined) {
            dismissToast(toastId);
            showToast('Error saving location.', TOAST_ERROR, FIVE_SECONDS);
          } else {
            await fetchAllLocations();
            setCurrentLocationId(result.data.id);
            dismissToast(toastId);
            showToast('Successfuly saved location.', TOAST_SUCCESS, FIVE_SECONDS);

            // check recent locations
            const found = recentLocations.findIndex(loc => loc.locationId === location.id);
            if (found > -1) {
              const copiedLocations = [...recentLocations];
              copiedLocations[found] = {
                ...location,
                locationId: location.id!,
                locationName: location.name,
              } as any;
              setRecentLocations(copiedLocations);
            }
          }
        }
      })
      .catch(e => {
        console.error(e);
      });
  });

  const handleGenerateTemplates = async () => {
    confirmAlert({
      customUI: ({ onClose }) => {
        return (
          <div className="alert-ui">
            <h3 className="alert">WARNING: Regenerating templates will override existing templates!!!</h3>
            <h4 className="text-center">Are you sure you want to continue?</h4>
            <div className="button-wrapper">
              <button className="button BKForm-btn-secondary mr-2" onClick={onClose}>
                Cancel
              </button>
              <button
                className="modern-btn warning"
                onClick={() => {
                  onClose();
                  showToast(TOAST_GENERATE_TEMPLATES_TEXT, TOAST_DEFAULT, FIVE_SECONDS);
                  if (currentLocationId) {
                    generateTemplate(currentLocationId);
                  }
                }}
              >
                Generate Templates
              </button>
            </div>
          </div>
        );
      },
    });
  };

  const handleOpenGenerateReportsModal = () => {
    setModalOpen(true);
  };

  const handleCloseGenerateReportsModal = () => {
    setModalOpen(false);
  };

  const handleAiGeneration = () => {
    generateAiPrompts({ locationId: currentLocationId! });
  };

  useEffect(() => {
    if (aiGenerationPrompt) {
      // Clear the field first
      setValue('description', '');

      // Typing animation
      let currentIndex = 0;
      const typingSpeed = 20; // milliseconds between characters

      const typeText = () => {
        if (currentIndex < aiGenerationPrompt.length) {
          const currentText = aiGenerationPrompt.substring(0, currentIndex + 1);
          setValue('description', currentText);
          currentIndex++;
          setTimeout(typeText, typingSpeed);
        } else {
          // Animation complete
          setShowAiSuggestions(false);
        }
      };

      // Start typing animation after a brief delay
      setTimeout(typeText, 100);
    }
  }, [aiGenerationPrompt, setValue]);

  useEffect(() => {
    // Reset state
    setId('');
    setTokenStatus('');
    setClassList([]);

    if (currentLocation) {
      // Update non-form state
      setId(currentLocation.id || '');
      setTokenStatus(currentLocation.tokenStatus || '');
      setClassList(currentLocation.classList || []);
      // Prepare comprehensive form data
      const formData: FormData = {
        name: currentLocation.name || '',
        org: currentLocation.org || '',

        weekStart: currentLocation.weekStart || 'Su',
        askCode: currentLocation.askCode || '',
        qboEnv: currentLocation.qboEnv || 'Prod',
        qboId: currentLocation.qboId || '',
        class: currentLocation.class || '',
        dept: currentLocation.dept || '',
        link: currentLocation.link || '',
        emailId: currentLocation.emailId || '',
        paymentLink: currentLocation.config?.links?.paymentLink || '',
        v2Link: currentLocation.config?.links?.v2 || '',
        payrollLink: currentLocation.config?.links?.payroll || '',
        tasksLink: currentLocation.config?.links?.tasks || '',
        teamsLink: currentLocation.config?.links?.teams || '',
        description: (currentLocation.config as any)?.description || '',
        config: {
          links: {
            link: currentLocation.link || '',
            paymentLink: currentLocation.config?.links?.paymentLink || '',
            v2Link: currentLocation.config?.links?.v2 || '',
            payrollLink: currentLocation.config?.links?.payroll || '',
            tasksLink: currentLocation.config?.links?.tasks || '',
            teamsLink: currentLocation.config?.links?.teams || '',
          },
          description: (currentLocation.config as any)?.description || '',
          companyName: currentLocation.config?.companyName || '',
          legalName: currentLocation.config?.legalName || '',
          transactionAttachments: currentLocation.config?.transactionAttachments || false,
          includeDeletedAccounts: currentLocation.config?.includeDeletedAccounts || false,
          website: currentLocation.config?.website || '',
          offlineVendorGLs: currentLocation.config?.offlineVendorGLs || '',
          rollfi: {
            env: (currentLocation.config as any)?.rollfi?.env || defaultRollfiEnv,
            companyId: (currentLocation.config as any)?.rollfi?.companyId || '',
          },
        },
      };

      // Reset form with populated data
      reset(formData);
    } else {
      // Reset form with default values when no location
      reset({
        name: '',
        org: '',
        weekStart: 'Su',
        askCode: '',
        qboEnv: 'Prod',
        qboId: '',
        class: '',
        dept: '',
        link: '',
        paymentLink: '',
        v2Link: '',
        payrollLink: '',
        tasksLink: '',
        teamsLink: '',
        description: '',
        config: {
          links: {
            link: '',
            paymentLink: '',
            v2Link: '',
            payrollLink: '',
            tasksLink: '',
            teamsLink: '',
          },
          description: '',
          companyName: '',
          legalName: '',
          rollfi: {
            env: defaultRollfiEnv,
            companyId: '',
          },
        },
      });
    }
  }, [currentLocation, reset]);

  if (locationsStatus === 'loading') {
    return (
      <div className="bookkeeper-tab-container">
        <Loading unmount={false} />
      </div>
    );
  }
  const prefix = prefixClass('bookkeeper-general-form');

  return (
    <div className={prefix()}>
      <div className={prefix('container')}>
        <Modal ariaHideApp={false} id="goal-modal" isOpen={modalOpen} onRequestClose={handleCloseGenerateReportsModal}>
          <div className="d-flex flex-column">
            <GenerateReportsForm currentLocation={currentLocation} onSuccess={handleCloseGenerateReportsModal} />
            <button className="button button_link" onClick={handleCloseGenerateReportsModal} type="button">
              Cancel
            </button>
          </div>
        </Modal>

        {(currentLocation?.id || currentLocationId === '') && (
          <div className={prefix('container-content')}>
            {/* Generate Actions */}
            <div className={prefix('container-content-action-section')}>
              <h4 className={prefix('container-content-action-title')}>Generate</h4>
              <div className={prefix('container-content-action-buttons')}>
                {/* Regenerate Buttons */}
                <button
                  className={prefix('container-content-buttons-actions report')}
                  onClick={handleOpenGenerateReportsModal}
                >
                  <div className={prefix('container-content-buttons-actions-icon')}>
                    <IconNavPlFilled />
                  </div>
                  <div className={prefix('container-content-buttons-actions-text')}>Reports</div>
                </button>

                <button
                  className={prefix('container-content-buttons-actions template')}
                  onClick={handleGenerateTemplates}
                >
                  <div className={prefix('container-content-buttons-actions-icon')}>
                    <IconViewData />
                  </div>
                  <div className={prefix('container-content-buttons-actions-text')}>Templates</div>
                </button>
              </div>
            </div>

            <form className={prefix('container-form')}>
              {/* Basic Information Section */}
              <div
                className={classNames(prefix('container-form-section'), {
                  'is-editing': editingSections.has('location-info'),
                })}
              >
                <div className={prefix('container-form-section-header')}>
                  <div className={prefix('container-form-section-header-item')}>
                    <IconPendoInfo />
                    <span>Location Information</span>
                  </div>
                  <div className={prefix('container-form-section-header-item')}>
                    <div className={prefix('container-form-section-header-item-actions')}>
                      <div className={prefix('container-form-section-header-item-actions-id')}>{id}</div>
                      <button
                        type="button"
                        className={prefix('container-form-section-header-item-actions-icon')}
                        onClick={() => handleCopyId()}
                        aria-label="Copy id"
                      >
                        <IconCopyNew />
                      </button>
                      <div className={prefix('container-form-section-header-item-actions-edit-container')}>
                        {!editingSections.has('location-info') ? (
                          <KSButton
                            onClick={() => startEditing('section', 'location-info')}
                            label=""
                            textAlign="center"
                            size="small"
                            color="tertiary"
                            className="edit-btn"
                            type="button"
                            icon={<IconEdit />}
                          />
                        ) : (
                          <>
                            <KSButton
                              onClick={handleSaveLocation}
                              label="SAVE"
                              textAlign="center"
                              rounded="medium"
                              size="small"
                              className="save-btn"
                              color="tertiary"
                              type="button"
                            />
                            <KSButton
                              onClick={() => stopEditingSection('location-info')}
                              label=""
                              textAlign="center"
                              rounded="large"
                              size="small"
                              color="tertiary"
                              className="cancel-btn"
                              type="button"
                              icon={<IconClose />}
                            />
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className={prefix('container-form-section-content')}>
                  <div className={prefix('container-form-section-content-row')}>
                    <div className={prefix('container-form-section-content-row-item')}>
                      <label htmlFor="name" id="name-label">
                        Name
                      </label>
                      <div className={prefix('container-form-section-content-row-item-field')}>
                        <Controller
                          name="name"
                          control={control}
                          render={({ field }) => (
                            <input
                              type="text"
                              id="name"
                              placeholder="Name of Location"
                              disabled={!editingSections.has('location-info')}
                              value={field.value}
                              onChange={field.onChange}
                            />
                          )}
                        />
                      </div>
                    </div>
                    <div className={prefix('container-form-section-content-row-item')}>
                      <label htmlFor="org" id="org-label">
                        Organization
                      </label>
                      <div className={prefix('container-form-section-content-row-item-field')}>
                        <Controller
                          name="org"
                          control={control}
                          render={({ field }) => (
                            <input
                              type="text"
                              id="org"
                              placeholder="Organization Name"
                              disabled={!editingSections.has('location-info')}
                              value={field.value}
                              onChange={field.onChange}
                            />
                          )}
                        />
                      </div>
                    </div>
                  </div>
                  <div className={prefix('container-form-section-content-row')}>
                    <div className={prefix('container-form-section-content-row-item full-width')}>
                      <label htmlFor="emailId" id="emailId-label">
                        Queue Inbox Email Id
                      </label>
                      <div className={prefix('container-form-section-content-row-item-field email-id-field')}>
                        <Controller
                          name="emailId"
                          control={control}
                          render={({ field }) => (
                            <input
                              type="text"
                              id="emailId"
                              placeholder="Email Id"
                              disabled={!editingSections.has('location-info')}
                              value={field.value}
                              onChange={field.onChange}
                            />
                          )}
                        />
                      </div>
                    </div>
                  </div>
                  <div className={prefix('container-form-section-content-row is-aligned')}>
                    <div className={prefix('container-form-section-content-row-item')}>
                      <label htmlFor="weekStart" id="weekStart-label">
                        Week Start
                      </label>
                      <Controller
                        name="weekStart"
                        control={control}
                        render={({ field }) => (
                          <select
                            id="weekStart"
                            className={`${prefix('container-form-section-content-row-item-field-select')}`}
                            disabled={!editingSections.has('location-info')}
                            value={field.value}
                            onChange={e => field.onChange(e.target.value)}
                          >
                            {weekDays.map(option => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        )}
                      />
                    </div>
                    <div className={prefix('container-form-section-content-row-item')}>
                      <label htmlFor="ask-code" id="ask-code-label">
                        TBC GL Accounts
                      </label>
                      <div className={prefix('container-form-section-content-row-item-field')}>
                        <Controller
                          name="askCode"
                          control={control}
                          render={({ field }) => (
                            <input
                              type="text"
                              id="ask-code"
                              placeholder="Ask GL Code"
                              disabled={!editingSections.has('location-info')}
                              value={field.value}
                              onChange={field.onChange}
                            />
                          )}
                        />
                      </div>
                    </div>
                    <div className={prefix('container-form-section-content-row-item')}>
                      <label htmlFor="offlineVendorGLs" id="offlineVendorGLs-label">
                        For Accounting GLs
                      </label>
                      <div className={prefix('container-form-section-content-row-item-field')}>
                        <Controller
                          name="config.offlineVendorGLs"
                          control={control}
                          render={({ field }) => (
                            <input
                              type="text"
                              id="offlineVendorGLs"
                              placeholder="For Accounting GLs"
                              disabled={!editingSections.has('location-info')}
                              value={field.value}
                              onChange={field.onChange}
                            />
                          )}
                        />
                      </div>
                    </div>
                  </div>
                  <div className={prefix('container-form-section-content-row')}>
                    <div className={prefix('container-form-section-content-row-item checkbox-container')}>
                      <Controller
                        name="config.includeDeletedAccounts"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="checkbox"
                            id="includeDeletedAccounts"
                            disabled={!editingSections.has('location-info')}
                            checked={field.value === true}
                            onChange={e => field.onChange(e.target.checked)}
                          />
                        )}
                      />
                      <label htmlFor="includeDeletedAccounts" id="includeDeletedAccounts-label">
                        Show Deleted GL Accounts
                      </label>
                    </div>
                    <div className={prefix('container-form-section-content-row-item checkbox-container')}>
                      <Controller
                        name="config.transactionAttachments"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="checkbox"
                            id="transactionAttachments"
                            disabled={!editingSections.has('location-info')}
                            checked={field.value === true}
                            onChange={e => field.onChange(e.target.checked)}
                          />
                        )}
                      />
                      <label htmlFor="transactionAttachments" id="transactionAttachments-label">
                        Include Transaction Attachments
                      </label>
                    </div>
                  </div>
                  <div className={prefix('container-form-section-content-row')}>
                    <div className={prefix('container-form-section-content-row-item')}>
                      <label htmlFor="links" id="links-label">
                        Website
                      </label>
                      <div className={prefix('container-form-section-content-row-item-field email-id-field')}>
                        <Controller
                          name="config.website"
                          control={control}
                          render={({ field }) => (
                            <input
                              type="text"
                              id="link"
                              placeholder="htttps://example.com"
                              disabled={!editingSections.has('location-info')}
                              value={field.value}
                              onChange={field.onChange}
                            />
                          )}
                        />
                      </div>
                    </div>
                  </div>
                  <div className={prefix('container-form-section-content-row is-last')}>
                    <div className={prefix('container-form-section-content-row-item')}>
                      <div className={prefix('container-form-section-content-row-item-header')}>
                        <label htmlFor="description" id="description-label">
                          AI Context & Instructions
                        </label>
                        {editingSections.has('location-info') && (
                          <button
                            type="button"
                            onClick={handleAiGeneration}
                            className="ai-suggestions-btn"
                            disabled={aiLoading}
                          >
                            {aiLoading ? 'Loading...' : '✨ AI Suggestions'}
                          </button>
                        )}
                      </div>
                      <div className={prefix('container-form-section-content-row-item-field')}>
                        <Controller
                          name="description"
                          control={control}
                          render={({ field }) => (
                            <textarea
                              id="description"
                              placeholder="Enter custom instructions for the AI model. This text will be used to guide AI responses and behavior specific to this location..."
                              disabled={!editingSections.has('location-info')}
                              value={field.value}
                              onChange={field.onChange}
                              rows={6}
                            />
                          )}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* QBO Configuration Section */}
              <div
                className={classNames(prefix('container-form-section'), {
                  'is-editing': editingSections.has('qbo-info'),
                })}
              >
                <div className={prefix('container-form-section-header')}>
                  <div className={prefix('container-form-section-header-item')}>
                    <IconQboNew />
                    <span>QuickBooks Online Configuration</span>
                  </div>
                  <div className={prefix('container-form-section-header-item right')}>
                    <span
                      className={prefix('container-form-section-header-item status')}
                      title={`Current Status: ${tokenStatus}`}
                    >
                      {tokenStatus}
                    </span>
                    <Whisper
                      placement="top"
                      trigger="hover"
                      delayOpen={600}
                      speaker={<Tooltip>{isProdHost() ? 'Refresh Token' : 'Sync Token'}</Tooltip>}
                    >
                      <span>
                        <KSButton
                          onClick={isProdHost() ? handleRefreshToken : handleSyncToken}
                          label={
                            <>
                              <IconRefresh />
                            </>
                          }
                          textAlign="center"
                          rounded="large"
                          size="small"
                          color="tertiary"
                          className="edit-btn"
                          type="button"
                        />
                      </span>
                    </Whisper>
                    <div className={prefix('container-form-section-header-item-actions')}>
                      {!editingSections.has('qbo-info') ? (
                        <KSButton
                          onClick={() => startEditing('section', 'qbo-info')}
                          label={
                            <>
                              <IconEdit />
                            </>
                          }
                          textAlign="center"
                          rounded="large"
                          size="small"
                          color="tertiary"
                          className="edit-btn"
                          type="button"
                        />
                      ) : (
                        <>
                          <KSButton
                            onClick={handleSaveLocation}
                            label={
                              <>
                                <span>SAVE</span>
                              </>
                            }
                            textAlign="center"
                            rounded="large"
                            size="large"
                            className="save-btn"
                            color="tertiary"
                            type="button"
                          />
                          <KSButton
                            onClick={() => stopEditingSection('qbo-info')}
                            label={
                              <>
                                <span>
                                  <IconClose />
                                </span>
                              </>
                            }
                            textAlign="center"
                            rounded="large"
                            size="large"
                            color="tertiary"
                            className="cancel-btn"
                            type="button"
                          />
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className={prefix('container-form-section-content')}>
                  <div className={prefix('container-form-section-content-row')}>
                    <div className={prefix('container-form-section-content-row-item')}>
                      <label htmlFor="company-name" id="company-name-label">
                        Company Name
                      </label>
                      <div className={prefix('container-form-section-content-row-item-field')}>
                        <Controller
                          name="config.companyName"
                          control={control}
                          render={({ field }) => (
                            <input
                              type="text"
                              id="config.companyName"
                              placeholder="QBO company name"
                              disabled
                              value={field.value}
                              onChange={field.onChange}
                              className="qbo-input"
                            />
                          )}
                        />
                      </div>
                    </div>
                    <div className={prefix('container-form-section-content-row-item')}>
                      <label htmlFor="legal-name" id="legal-name-label">
                        Legal Name
                      </label>
                      <div className={prefix('container-form-section-content-row-item-field')}>
                        <Controller
                          name="config.legalName"
                          control={control}
                          render={({ field }) => (
                            <input
                              type="text"
                              id="config.legalName"
                              placeholder="QBO legal name"
                              disabled
                              value={field.value}
                              onChange={field.onChange}
                              className="qbo-input"
                            />
                          )}
                        />
                      </div>
                    </div>
                  </div>
                  <div className={prefix('container-form-section-content-row is-last')}>
                    <div className={prefix('container-form-section-content-row-item')}>
                      <label htmlFor="qbo-id" id="qbo-id-label">
                        QBO Company ID
                      </label>
                      <div className={prefix('container-form-section-content-row-item-field')}>
                        <Controller
                          name="qboId"
                          control={control}
                          render={({ field }) => (
                            <input
                              type="text"
                              id="qbo-id"
                              placeholder="QBO Company ID"
                              disabled={!editingSections.has('qbo-info')}
                              value={field.value}
                              onChange={field.onChange}
                            />
                          )}
                        />
                      </div>
                    </div>
                    <div className={prefix('container-form-section-content-row-item')}>
                      <label htmlFor="qbo-env" id="qbo-env-label">
                        QBO Environment
                      </label>
                      <div className={prefix('container-form-section-content-row-item-field')}>
                        <Controller
                          name="qboEnv"
                          control={control}
                          render={({ field }) => (
                            <select
                              id="qbo-env"
                              disabled={!editingSections.has('qbo-info')}
                              value={field.value}
                              onChange={field.onChange}
                              className={`${prefix('container-form-section-content-row-item-field-select')}`}
                            >
                              <option value="Prod">Production</option>
                              <option value="Sandbox">Sandbox</option>
                            </select>
                          )}
                        />
                      </div>
                    </div>

                    <div className={prefix('container-form-section-content-row-item')}>
                      <label htmlFor="class" id="class-label">
                        Class
                      </label>
                      <div className={prefix('container-form-section-content-row-item-field with-button')}>
                        <Controller
                          name="class"
                          control={control}
                          render={({ field }) => (
                            <select
                              id="class"
                              disabled={!editingSections.has('qbo-info')}
                              value={field.value}
                              onChange={field.onChange}
                              className={`${prefix('container-form-section-content-row-item-field-select')}`}
                            >
                              <option value="">Select a class...</option>
                              {classList.map(locClass => (
                                <option value={locClass.id} key={locClass.id}>
                                  {locClass.name}
                                </option>
                              ))}
                            </select>
                          )}
                        />
                        <KSButton
                          onClick={handleGetClasses}
                          label={
                            <>
                              <IconDownload />
                            </>
                          }
                          textAlign="center"
                          rounded="large"
                          size="small"
                          color="tertiary"
                          className="edit-btn"
                          type="button"
                          tooltip='Get classes from QBO (make sure token is "Connected")'
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Links & Integrations Section */}
              <div
                className={classNames(prefix('container-form-section'), {
                  'is-editing': editingSections.has('links-info'),
                })}
              >
                <div className={prefix('container-form-section-header')}>
                  <div className={prefix('container-form-section-header-item')}>
                    <IconLink />
                    <span>Links & Integrations</span>
                  </div>
                  <div className={prefix('container-form-section-header-item right')}>
                    <div className={prefix('container-form-section-header-item-actions')}>
                      <div className={prefix('container-form-section-header-item-actions-edit-container')}>
                        {!editingSections.has('links-info') ? (
                          <KSButton
                            onClick={() => startEditing('section', 'links-info')}
                            label=""
                            textAlign="center"
                            size="small"
                            color="tertiary"
                            className="edit-btn"
                            type="button"
                            icon={<IconEdit />}
                          />
                        ) : (
                          <>
                            <KSButton
                              onClick={handleSaveLocation}
                              label="SAVE"
                              textAlign="center"
                              rounded="medium"
                              size="small"
                              className="save-btn"
                              color="tertiary"
                              type="button"
                            />
                            <KSButton
                              onClick={() => stopEditingSection('links-info')}
                              label=""
                              textAlign="center"
                              rounded="large"
                              size="large"
                              color="tertiary"
                              className="cancel-btn"
                              type="button"
                              icon={<IconClose />}
                            />
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className={prefix('container-form-section-content')}>
                  <div className={prefix('container-form-section-content-row')}>
                    <div className={prefix('container-form-section-content-row-item')}>
                      <label htmlFor="link" id="link-label">
                        Demo Link
                      </label>
                      <div className={prefix('container-form-section-content-row-item-field')}>
                        <Controller
                          name="link"
                          control={control}
                          render={({ field }) => (
                            <input
                              type="text"
                              id="link"
                              placeholder="Another Location's ID"
                              disabled={!editingSections.has('links-info')}
                              value={field.value}
                              onChange={field.onChange}
                            />
                          )}
                        />
                      </div>
                    </div>
                    <div className={prefix('container-form-section-content-row-item')}>
                      <label htmlFor="paymentLink" id="paymentLink-label">
                        Pay Bills Link
                      </label>
                      <div className={prefix('container-form-section-content-row-item-field')}>
                        <Controller
                          name="paymentLink"
                          control={control}
                          render={({ field }) => (
                            <input
                              type="text"
                              id="paymentLink"
                              placeholder="Payment provider's link"
                              disabled={!editingSections.has('links-info')}
                              value={field.value}
                              onChange={field.onChange}
                            />
                          )}
                        />
                      </div>
                    </div>
                    <div className={prefix('container-form-section-content-row-item')}>
                      <label htmlFor="v2Link" id="v2Link-label">
                        V2 Link
                      </label>
                      <div className={prefix('container-form-section-content-row-item-field')}>
                        <Controller
                          name="v2Link"
                          control={control}
                          render={({ field }) => (
                            <input
                              type="text"
                              id="v2Link"
                              placeholder="V2 Link"
                              disabled={!editingSections.has('links-info')}
                              value={field.value}
                              onChange={field.onChange}
                            />
                          )}
                        />
                      </div>
                    </div>
                  </div>
                  <div className={prefix('container-form-section-content-row is-last')}>
                    <div className={prefix('container-form-section-content-row-item')}>
                      <label htmlFor="payrollLink" id="payrollLink-label">
                        Payroll Link
                      </label>
                      <div className={prefix('container-form-section-content-row-item-field')}>
                        <Controller
                          name="payrollLink"
                          control={control}
                          render={({ field }) => (
                            <input
                              type="text"
                              id="payrollLink"
                              placeholder="Payroll link"
                              disabled={!editingSections.has('links-info')}
                              value={field.value}
                              onChange={field.onChange}
                            />
                          )}
                        />
                      </div>
                    </div>
                    <div className={prefix('container-form-section-content-row-item')}>
                      <label htmlFor="tasksLink" id="tasksLink-label">
                        Tasks Link
                      </label>
                      <div className={prefix('container-form-section-content-row-item-field')}>
                        <Controller
                          name="tasksLink"
                          control={control}
                          render={({ field }) => (
                            <input
                              type="text"
                              id="tasksLink"
                              placeholder="Tasks Link"
                              disabled={!editingSections.has('links-info')}
                              value={field.value}
                              onChange={field.onChange}
                            />
                          )}
                        />
                      </div>
                    </div>
                    <div className={prefix('container-form-section-content-row-item')}>
                      <label htmlFor="teamsLink" id="teamsLink-label">
                        Teams Link
                      </label>
                      <div className={prefix('container-form-section-content-row-item-field')}>
                        <Controller
                          name="teamsLink"
                          control={control}
                          render={({ field }) => (
                            <input
                              type="text"
                              id="teamsLink"
                              placeholder="Teams Link"
                              disabled={!editingSections.has('links-info')}
                              value={field.value}
                              onChange={field.onChange}
                            />
                          )}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Rollfi Section */}
              <div
                className={classNames(prefix('container-form-section'), {
                  'is-editing': editingSections.has('kitchensync-payroll-info'),
                })}
              >
                <div className={prefix('container-form-section-header')}>
                  <div className={prefix('container-form-section-header-item')}>
                    <IconViewData />
                    <span>KitchenSync Payroll</span>
                  </div>
                  <div className={prefix('container-form-section-header-item right')}>
                    <div className={prefix('container-form-section-header-item-actions')}>
                      <div className={prefix('container-form-section-header-item-actions-edit-container')}>
                        {/* <Controller
                          name="config.rollfi.env"
                          control={control}
                          render={({ field }) => (
                            <>
                              <span>{field.value === 'prod' ? 'Prod' : 'Dev'}</span>
                              {editingSections.has('kitchensync-payroll-info') && (
                                <KSToggleButton
                                  value={field.value === 'prod'}
                                  onChange={isProd => field.onChange(isProd ? 'prod' : 'dev')}
                                />
                              )}
                            </>
                          )}
                        /> */}
                        {!editingSections.has('kitchensync-payroll-info') ? (
                          <KSButton
                            onClick={() => startEditing('section', 'kitchensync-payroll-info')}
                            label=""
                            textAlign="center"
                            size="small"
                            color="tertiary"
                            className="edit-btn"
                            type="button"
                            icon={<IconEdit />}
                          />
                        ) : (
                          <>
                            <KSButton
                              onClick={handleSaveLocation}
                              label="SAVE"
                              textAlign="center"
                              rounded="medium"
                              size="small"
                              className="save-btn"
                              color="tertiary"
                              type="button"
                            />
                            <KSButton
                              onClick={() => stopEditingSection('kitchensync-payroll-info')}
                              label=""
                              textAlign="center"
                              rounded="large"
                              size="small"
                              color="tertiary"
                              className="cancel-btn"
                              type="button"
                              icon={<IconClose />}
                            />
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className={prefix('container-form-section-content')}>
                  <div className={prefix('container-form-section-content-row')}>
                    <div className={prefix('container-form-section-content-row-item')}>
                      <label htmlFor="rollfiCompanyId" id="rollfiCompanyId-label">
                        Company
                      </label>
                      <div className={prefix('container-form-section-content-row-item-field')}>
                        <Controller
                          name="config.rollfi.companyId"
                          control={control}
                          render={({ field }) => (
                            <select
                              id="rollfiCompanyId"
                              disabled={!editingSections.has('kitchensync-payroll-info')}
                              value={field.value}
                              onChange={field.onChange}
                              className={`${prefix('container-form-section-content-row-item-field-select')} big`}
                            >
                              <option value="">Select a company...</option>
                              {rollfiCompaniesData?.map((company: RollfiCompany) => (
                                <option key={company.companyID} value={company.companyID}>
                                  {company.company}
                                </option>
                              ))}
                            </select>
                          )}
                        />
                      </div>
                    </div>
                    <div className={prefix('container-form-section-content-row-item')}>
                      <label htmlFor="rollfiCompanyIdDisplay" id="rollfiCompanyIdDisplay-label">
                        Company ID
                      </label>
                      <div className={prefix('container-form-section-content-row-item-field')}>
                        <Controller
                          name="config.rollfi.companyId"
                          control={control}
                          render={({ field }) => (
                            <>
                              <input
                                type="text"
                                id="rollfiCompanyIdDisplay"
                                className="qbo-input"
                                placeholder="Company ID"
                                disabled
                                value={field.value}
                                readOnly
                              />
                              {field.value &&
                                rollfiCompaniesData &&
                                !rollfiCompaniesData.some((c: RollfiCompany) => c.companyID === field.value) && (
                                  <span className="rollfi-warning">Unrecognized Company ID</span>
                                )}
                            </>
                          )}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}

const BookkeeperLocationFormWrapped = () => (
  <LocalErrorBoundary>
    <BookkeeperLocationForm />
  </LocalErrorBoundary>
);

export default BookkeeperLocationFormWrapped;
